import { useEffect } from "react"
import { useAppState } from "../../context/appStateContext"
import { PurchasProvider, usePurchase } from "../../context/purchaseContext"
import {
  UltimateAlertsProvider,
  useUltimateAlerts,
} from "../../context/ultimateAlertsContext"
import FullScreenPage from "../../components/FullScreenPage/FullScreenPage"
import FixedCloseButton from "../../components/FixedCloseButton/FixedCloseButton"
import { useComparePlansFeatures } from "../../components/ComparePlansSheet/useComparePlansFeatures"
import UltimateSubfeaturesPopups from "../../components/UltimateSubfeaturesPopups/UltimateSubfeaturesPopups"
import native from "../../native/native"
import { formatCurrency, formatProductPrice } from "../../utils/utils"
import { Plan, Product, SubscriptionDuration } from "../../appData/productData"
import images from "../../utils/images"
import useStrings from "../../localization/useStrings"
import useQueryParams from "../../hooks/useQueryParams"
import styles from "./OnboardingUltimateAlternatePaywall.module.scss"
import {
  TrophyIcon,
  CrownIcon,
  BellIcon,
  DollarIcon,
} from "./OnboardingUltimateAlternateIcons"

const OnboardingUltimateAlternatePaywall = () => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <PaywallContent />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

const PaywallContent = () => {
  const { state, actions } = useAppState()
  const { state: purchaseState, actions: purchaseActions } = usePurchase()

  useEffect(() => {
    actions.setIgnoreFooterInset(true)
    actions.setIgnoreHeaderInset(true)
  }, [actions])

  const handleClose = () => {
    native.postNativeEvent("close")
  }

  const handlePlanSelect = (isUltimate: boolean) => {
    purchaseActions.setUltimateSelected(isUltimate)
  }

  const handlePricingSelect = (pricing: SubscriptionDuration) => {
    purchaseActions.setYearlySelected(pricing === "yearly")
  }

  const handleStartTrial = () => {
    purchaseActions.purchaseCurrent()
  }

  const isUltimate = purchaseState.ultimateSelected

  // Derive selected pricing from the current product
  const selectedPricing = purchaseState.selectedProduct.duration

  console.log(state.initialData)

  return (
    <FullScreenPage addInsetPadding={false}>
      <div
        className={styles.container}
        style={{ paddingTop: state.initialData?.originalTopInset ?? 0 }}
      >
        <FixedCloseButton
          onClick={handleClose}
          topInset={state.initialData?.originalTopInset ?? 0}
        />
        <div className={styles.scrollContainer}>
          <div className={styles.content}>
            <Header />
            <PlanCards
              isUltimate={isUltimate}
              onPlanSelect={handlePlanSelect}
            />
            <ComparisonTable />
            <InfoCard />
            <PricingOptions
              selectedPricing={selectedPricing}
              onPricingSelect={handlePricingSelect}
              plan={purchaseState.selectedPlan}
            />
          </div>
        </div>
        <StickyFooter onStartTrial={handleStartTrial} />
      </div>
      <UltimateSubfeaturesPopups />
    </FullScreenPage>
  )
}

const Header = () => {
  const strings = useStrings()

  return (
    <div className={styles.header}>
      <h1>{strings.onboardingUltimateAlternate.title}</h1>
    </div>
  )
}

interface PlanCardsProps {
  isUltimate: boolean
  onPlanSelect: (isUltimate: boolean) => void
}

const PlanCards = ({ isUltimate, onPlanSelect }: PlanCardsProps) => {
  const { state: appState } = useAppState()
  const productData = appState.productData
  const queryParams = useQueryParams()

  if (!productData) {
    return null
  }

  const premiumCard = (
    <PlanCard
      isUltimate={false}
      isActive={!isUltimate}
      onClick={() => onPlanSelect(false)}
      showTimeline={!isUltimate}
      yearlyProduct={productData.premium.yearlyProduct}
    />
  )

  const ultimateCard = (
    <PlanCard
      isUltimate={true}
      isActive={isUltimate}
      onClick={() => onPlanSelect(true)}
      showTimeline={isUltimate}
      yearlyProduct={productData.ultimate.yearlyProduct}
    />
  )

  // Show Ultimate first if plan=ultimate query param is set
  if (queryParams.isUltimate) {
    return (
      <>
        {ultimateCard}
        {premiumCard}
      </>
    )
  }

  // Default order: Premium first
  return (
    <>
      {premiumCard}
      {ultimateCard}
    </>
  )
}

interface PlanCardProps {
  isUltimate: boolean
  isActive: boolean
  onClick: () => void
  showTimeline: boolean
  yearlyProduct: Product
}

const PlanCard = ({
  isUltimate,
  isActive,
  onClick,
  showTimeline,
  yearlyProduct,
}: PlanCardProps) => {
  const strings = useStrings()
  const isPremium = !isUltimate
  const cardClassName = isPremium
    ? `${styles.planCard} ${isActive ? styles.active : ""}`
    : `${styles.planCard} ${styles.ultimate} ${isActive ? styles.activeUltimate : ""}`

  const yearlyPrice = formatProductPrice(yearlyProduct)
  const monthlyFromYearly = formatCurrency(
    yearlyProduct.price / 12,
    yearlyProduct.priceLocale,
  )

  const planConfig = isPremium
    ? {
        icon: <TrophyIcon />,
        title: "Premium",
        description: strings.onboardingUltimateAlternate.premiumDescription,
        badge: null,
        headerStyle: {},
      }
    : {
        icon: <CrownIcon />,
        title: "Ultimate",
        description: strings.onboardingUltimateAlternate.ultimateDescription,
        badge: (
          <div className={styles.ultimateBadge}>
            {strings.onboardingUltimateAlternate.includesPremium}
          </div>
        ),
        headerStyle: { marginBottom: 0 },
      }

  return (
    <div className={cardClassName} onClick={onClick}>
      <div className={isPremium ? undefined : styles.ultimateHeader}>
        <div className={styles.planHeader} style={planConfig.headerStyle}>
          <div className={styles.planIcon}>{planConfig.icon}</div>
          <label className={styles.planTitle}>{planConfig.title}</label>
        </div>
        {planConfig.badge}
      </div>
      <label className={styles.planDescription}>
        {planConfig.description}
        <br />
        <strong>
          {strings.onboardingUltimateAlternate.trialPricing(
            yearlyPrice,
            monthlyFromYearly,
          )}
        </strong>
      </label>
      {showTimeline && (
        <Timeline isPremium={isPremium} yearlyPrice={yearlyPrice} />
      )}
    </div>
  )
}

interface TimelineProps {
  isPremium: boolean
  yearlyPrice: string
}

const Timeline = ({ isPremium, yearlyPrice }: TimelineProps) => {
  const strings = useStrings()
  const iconClass = isPremium
    ? styles.timelineIcon
    : `${styles.timelineIcon} ${styles.ultimateColor}`

  return (
    <div className={styles.timeline}>
      <div className={styles.timelineItem}>
        <div className={iconClass}>
          <TrophyIcon />
        </div>
        <div className={styles.timelineText}>
          <strong>{strings.onboardingUltimateAlternate.timeline.today}</strong>{" "}
          {strings.onboardingUltimateAlternate.timeline.todayDescription}
        </div>
      </div>
      <div className={styles.timelineItem}>
        <div className={iconClass}>
          <BellIcon />
        </div>
        <div className={styles.timelineText}>
          <strong>{strings.onboardingUltimateAlternate.timeline.day5}</strong>{" "}
          {strings.onboardingUltimateAlternate.timeline.day5Description}
        </div>
      </div>
      <div className={styles.timelineItem}>
        <div className={iconClass}>
          <DollarIcon />
        </div>
        <div className={styles.timelineText}>
          <strong>{strings.onboardingUltimateAlternate.timeline.day7}</strong>{" "}
          {strings.onboardingUltimateAlternate.timeline.day7Description(
            yearlyPrice,
          )}
        </div>
      </div>
    </div>
  )
}

const ComparisonTable = () => {
  const strings = useStrings()
  const features = useComparePlansFeatures()
  const { actions } = useUltimateAlerts()

  let displayFeatures = features.filter(
    (f) => f.type === "premium" || f.type === "ultimate",
  )

  const freeFeatures = features.filter((f) => f.type === "free")

  for (const freeFeature of freeFeatures) {
    freeFeature.type = "premium"
  }

  displayFeatures = [...freeFeatures, ...displayFeatures]

  const renderCheckmark = (text?: string) => {
    if (!text) return null
    const isIcon = text === "✓"
    return (
      <span
        className={`${styles.checkmark} ${isIcon ? styles.checkMarkIcon : ""}`}
      >
        {text}
      </span>
    )
  }

  return (
    <div className={styles.comparisonTable}>
      <div className={styles.tableRow}>
        <div className={styles.tableCell}>
          {strings.onboardingUltimateAlternate.comparisonTable.header}
        </div>
        <div className={styles.tableCell}>Premium</div>
        <div className={styles.tableCell}>Ultimate</div>
      </div>
      {displayFeatures.map((feature, index) => {
        const hasPremium = feature.type === "premium"
        const hasUltimate =
          feature.type === "ultimate" || feature.type === "premium"

        return (
          <div key={index} className={styles.tableRow}>
            <div className={styles.tableCell}>
              {feature.name}
              {feature.alert && (
                <button
                  className={styles.infoButton}
                  onClick={() => actions.setShowFeatureAlert(feature.alert!)}
                >
                  <div className={images.info.class} />
                </button>
              )}
            </div>
            <div className={styles.tableCell}>
              {hasPremium && renderCheckmark(feature.premiumText || "✓")}
            </div>
            <div className={styles.tableCell}>
              {hasUltimate && renderCheckmark(feature.ultimateText || "✓")}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const InfoCard = () => {
  const strings = useStrings()

  return (
    <div style={{ marginBottom: "24px" }}>
      <div className={styles.didYouKnowLabel}>
        <p>{strings.onboardingUltimateAlternate.didYouKnow.label}</p>
      </div>
      <div className={styles.infoCard}>
        <div className={styles.infoContent}>
          <div className={styles.infoNumber}>
            {strings.onboardingUltimateAlternate.didYouKnow.statNumber}
          </div>
          <div className={styles.infoText}>
            {strings.onboardingUltimateAlternate.didYouKnow.statDescription}
          </div>
        </div>
      </div>
    </div>
  )
}

interface PricingOptionsProps {
  selectedPricing: SubscriptionDuration
  onPricingSelect: (pricing: SubscriptionDuration) => void
  plan: Plan
}

const PricingOptions = ({
  selectedPricing,
  onPricingSelect,
  plan,
}: PricingOptionsProps) => {
  const strings = useStrings()
  const yearlyProduct = plan.yearlyProduct
  const monthlyProduct = plan.monthlyProduct

  const yearlyPrice = formatProductPrice(yearlyProduct)
  const monthlyFromYearly = formatCurrency(
    yearlyProduct.price / 12,
    yearlyProduct.priceLocale,
  )
  const monthlyPrice = formatProductPrice(monthlyProduct)

  const pricingOptions = [
    {
      type: "yearly" as SubscriptionDuration,
      title: strings.onboardingUltimateAlternate.pricing.yearlyPlanTitle,
      subtitle:
        strings.onboardingUltimateAlternate.pricing.yearlyPlanSubtitle(
          yearlyPrice,
        ),
      amount:
        strings.onboardingUltimateAlternate.pricing.yearlyAmount(
          monthlyFromYearly,
        ),
      showBadge: true,
    },
    {
      type: "monthly" as SubscriptionDuration,
      title: strings.onboardingUltimateAlternate.pricing.monthlyTitle,
      subtitle: null,
      amount:
        strings.onboardingUltimateAlternate.pricing.monthlyAmount(monthlyPrice),
      showBadge: false,
    },
  ]

  return (
    <>
      {pricingOptions.map((option) => (
        <div
          key={option.type}
          className={`${styles.pricingOption} ${selectedPricing === option.type ? styles.active : ""}`}
          onClick={() => onPricingSelect(option.type)}
        >
          {option.showBadge && (
            <div className={styles.popularBadge}>
              {strings.onboardingUltimateAlternate.pricing.popularBadge}
            </div>
          )}
          <div className={styles.pricingContent}>
            <div className={styles.pricingLeft}>
              <label className={styles.pricingLeftTitle}>{option.title}</label>
              {option.subtitle && (
                <label className={styles.pricingLeftSubtitle}>
                  {option.subtitle}
                </label>
              )}
            </div>
            <div className={styles.pricingRight}>
              <div className={styles.pricingAmount}>{option.amount}</div>
              <div className={styles.radioCheck}>
                <span className={styles.checkmark}>✓</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

interface StickyFooterProps {
  onStartTrial: () => void
}

const StickyFooter = ({ onStartTrial }: StickyFooterProps) => {
  const strings = useStrings()
  const { state: appState } = useAppState()
  const bottomInset = (appState.initialData?.originalBottomInset ?? 0) + 8

  return (
    <div className={styles.stickyFooter}>
      <button className={styles.ctaButton} onClick={onStartTrial}>
        {strings.onboardingUltimateAlternate.ctaButton}
      </button>
      <div
        className={styles.footerInset}
        style={{ height: `${bottomInset}px` }}
      />
    </div>
  )
}

export default OnboardingUltimateAlternatePaywall
